import { collection, doc, addDoc, getDoc, getDocs, updateDoc, setDoc, deleteDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase'; // Adjust this path if your firebase.js is in a different folder

// ==========================================
// 1099 CONTRACTOR ENGINE (MULTI-TENANT)
// ==========================================

export async function createTenantContractor(tenantId, contractorPayload) {
  if (!tenantId) throw new Error("Unauthorized: Missing Tenant Context.");
  const contractorsRef = collection(db, `clients/${tenantId}/crm_contractors`);
  const docRef = await addDoc(contractorsRef, {
    ...contractorPayload,
    tenantId,
    status: 'Active',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
}

export async function getTenantContractors(tenantId) {
  if (!tenantId) throw new Error("Unauthorized: Missing Tenant Context.");
  const contractorsRef = collection(db, `clients/${tenantId}/crm_contractors`);
  const snapshot = await getDocs(contractorsRef);
  return snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
}

export async function getTenantContractor(tenantId, contractorId) {
  if (!tenantId) throw new Error("Unauthorized: Missing Tenant Context.");
  const docRef = doc(db, `clients/${tenantId}/crm_contractors`, contractorId);
  const snap = await getDoc(docRef);
  if (snap.exists()) {
    return { id: snap.id, ...snap.data() };
  }
  return null;
}

export async function updateTenantContractor(tenantId, contractorId, updates) {
  if (!tenantId) throw new Error("Unauthorized: Missing Tenant Context.");
  const docRef = doc(db, `clients/${tenantId}/crm_contractors`, contractorId);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });
}

// ==========================================
// UNIFIED PIPELINE & LEADS ENGINE (MULTI-TENANT)
// ==========================================

export async function createTenantLead(tenantId, leadPayload) {
  if (!tenantId) throw new Error("Unauthorized: Missing Tenant Context.");
  const leadsRef = collection(db, `clients/${tenantId}/crm_leads`);
  const docRef = await addDoc(leadsRef, {
    ...leadPayload,
    tenantId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
}

export async function getTenantLeads(tenantId) {
  if (!tenantId) throw new Error("Unauthorized: Missing Tenant Context.");
  
  // Dual Listeners: Query both inbound web traffic and manual CRM efforts
  const manualLeadsRef = collection(db, `clients/${tenantId}/crm_leads`);
  const inboundPipelineRef = collection(db, `clients/${tenantId}/pipeline`);

  // Fetch both collections concurrently for zero-latency merging
  const [manualSnap, inboundSnap] = await Promise.all([
    getDocs(manualLeadsRef),
    getDocs(inboundPipelineRef)
  ]);

  const manualLeads = manualSnap.docs.map(docSnap => ({ 
    id: docSnap.id, 
    ...docSnap.data(),
    sourceCollection: 'crm_leads',
    sourceType: 'Manual Lead'
  }));

  const inboundLeads = inboundSnap.docs.map(docSnap => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      sourceCollection: 'pipeline',
      sourceType: data.type || 'Website Inquiry'
    };
  });

  // Merge and return the unified array to the frontend
  return [...manualLeads, ...inboundLeads];
}

export async function getTenantLead(tenantId, leadId, collectionName = 'crm_leads') {
  if (!tenantId) throw new Error("Unauthorized: Missing Tenant Context.");
  const docRef = doc(db, `clients/${tenantId}/${collectionName}`, leadId);
  const snap = await getDoc(docRef);
  if (snap.exists()) {
    return { id: snap.id, ...snap.data(), sourceCollection: collectionName };
  }
  return null;
}

export async function updateTenantLead(tenantId, leadId, updates, collectionName = 'crm_leads') {
  if (!tenantId) throw new Error("Unauthorized: Missing Tenant Context.");
  // Routes the update to the correct collection based on where the lead originated
  const docRef = doc(db, `clients/${tenantId}/${collectionName}`, leadId);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });
}

export async function deleteTenantLead(tenantId, leadId, collectionName = 'crm_leads') {
  if (!tenantId) throw new Error("Unauthorized: Missing Tenant Context.");
  const docRef = doc(db, `clients/${tenantId}/${collectionName}`, leadId);
  await deleteDoc(docRef);
}

// ==========================================
// W-2 WORKFORCE ENGINE (MULTI-TENANT)
// ==========================================

export async function createTenantW2Employee(tenantId, employeePayload) {
  if (!tenantId) throw new Error("Unauthorized: Missing Tenant Context.");
  
  const employeesRef = collection(db, `clients/${tenantId}/w2_employees`);
  const docRef = await addDoc(employeesRef, {
    ...employeePayload,
    tenantId,
    status: 'Active',
    checkhq_id: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  
  return docRef.id;
}

export async function getTenantW2Employees(tenantId) {
  if (!tenantId) throw new Error("Unauthorized: Missing Tenant Context.");
  
  const employeesRef = collection(db, `clients/${tenantId}/w2_employees`);
  const snapshot = await getDocs(employeesRef);
  
  return snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
}

export async function getTenantW2Employee(tenantId, employeeId) {
  if (!tenantId) throw new Error("Unauthorized: Missing Tenant Context.");
  
  const docRef = doc(db, `clients/${tenantId}/w2_employees`, employeeId);
  const snap = await getDoc(docRef);
  
  if (snap.exists()) {
    return { id: snap.id, ...snap.data() };
  }
  
  return null;
}

export async function updateTenantW2Employee(tenantId, employeeId, updates) {
  if (!tenantId) throw new Error("Unauthorized: Missing Tenant Context.");
  
  const docRef = doc(db, `clients/${tenantId}/w2_employees`, employeeId);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });
}

// ==========================================
// TIME & ATTENDANCE ENGINE (MULTI-TENANT)
// ==========================================

export async function getTenantTimesheets(tenantId, startDate, endDate) {
  if (!tenantId) throw new Error("Unauthorized: Missing Tenant Context.");
  
  const timesheetsRef = collection(db, `clients/${tenantId}/timesheets`);
  const snapshot = await getDocs(timesheetsRef);
  return snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
}

// ==========================================
// COMMISSION & PAYROLL ENGINE (MULTI-TENANT)
// ==========================================

export async function getOperatorPulse(tenantId, uid) {
  if (!tenantId || !uid) throw new Error("Unauthorized: Missing Context.");
  
  const docRef = doc(db, `clients/${tenantId}/operator_stats`, uid);
  const snap = await getDoc(docRef);
  
  if (snap.exists()) {
    return snap.data();
  }
  return { tier: 'Standard 1099', pendingPayout: 0, mtdCommission: 0, ytdEarnings: 0 };
}

export async function getOperatorCommissions(tenantId, uid) {
  if (!tenantId || !uid) throw new Error("Unauthorized: Missing Context.");
  
  const commRef = collection(db, `clients/${tenantId}/commissions`);
  const q = query(commRef, where("operatorId", "==", uid));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
}

export async function getPendingLiabilities(tenantId) {
  if (!tenantId) throw new Error("Unauthorized: Missing Tenant Context.");
  
  const liabilitiesRef = collection(db, `clients/${tenantId}/pending_liabilities`);
  const snapshot = await getDocs(liabilitiesRef);
  
  return snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
}

export async function getCommissionRules(tenantId) {
  if (!tenantId) throw new Error("Unauthorized: Missing Tenant Context.");
  
  const docRef = doc(db, `clients/${tenantId}/config`, 'commission_rules');
  const snap = await getDoc(docRef);
  
  return snap.exists() ? snap.data() : null;
}

export async function saveCommissionRules(tenantId, rules) {
  if (!tenantId) throw new Error("Unauthorized: Missing Tenant Context.");
  
  const docRef = doc(db, `clients/${tenantId}/config`, 'commission_rules');
  await setDoc(docRef, { 
    ...rules, 
    updatedAt: serverTimestamp() 
  }, { merge: true });
}